import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, ButtonModel, IconRegularCalendar, IconRegularPen, IconRegularPerson } from '@notino/react-styleguide'
import dayjs from 'dayjs'

// types
import { ReservationResponse, SalonServicesResponse } from '../../../../types/types'

// utils
import { formatReservationDateRange, getDateTime, getEmployeeName, getGoogleMapsLink, PAGE_LINKS } from '../../../../utils/helper'
import { EVENT_TYPE, RESERVATION_STATE } from '../../../../utils/enums'

// components
import SalonInfo from '../SalonInfo/SalonInfo'
import ReservationStatusBadge from '../../../../components/ReservationStatusBadge/ReservationStatusBadge'
import AddToCalendarModal from '../../../../components/AddToCalendarModal/AddToCalendarModal'

// assets
import ScissorsIcon from '../../../../assets/icons/ScissorsIcon'
import IconRegularGuest from '../../../../assets/icons/IconRegularGuest'
import UsersIcon from '../../../../assets/icons/UsersIcon'

// styles
import * as SC from './ReservationStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	reservation: NonNullable<ReservationResponse['reservation']>
	queryParamsToken?: string
	salonServicesData: SalonServicesResponse | undefined
	onBookAgain: (reservation: NonNullable<ReservationResponse['reservation']>) => void
	onCancelReservation: () => void
}

const Reservation = (props: Props) => {
	const { reservation, queryParamsToken, onCancelReservation, onBookAgain, salonServicesData } = props
	const { locale, formatDate, formatMessage } = useIntl()
	const { messages } = useMessages()

	const [isAddToCalendarModalOpen, setIsAddToCalendarModalOpen] = useState(false)

	const googleMapsLink = getGoogleMapsLink({
		lat: reservation.calendarEvent.salon.address?.latitude,
		lon: reservation.calendarEvent.salon.address?.longitude
	})

	const salonPhoneNumber = reservation.calendarEvent.salon.phones[0]
		? `${reservation.calendarEvent.salon.phones[0].phonePrefix}${reservation.calendarEvent.salon.phones[0].phone}`
		: ''

	const dateTimeRange = formatReservationDateRange(
		formatDate,
		getDateTime(reservation.calendarEvent.start.date, reservation.calendarEvent.start.time).toDate(),
		getDateTime(reservation.calendarEvent.end.date, reservation.calendarEvent.end.time).toDate()
	)

	const employeeName = getEmployeeName(reservation.calendarEvent.employee)

	const serviceCategoryParameterValue = reservation.calendarEvent.serviceCategoryParameterValue?.categoryParameterValue
	const serviceParameterLabel = serviceCategoryParameterValue?.value
		? `${serviceCategoryParameterValue.categoryParameter.name}: ${serviceCategoryParameterValue.value}`
		: undefined

	const getReservationStateSpecificProps = (): {
		badge: React.ReactNode
		title: string | undefined
		description: string | undefined
		rightCtaButtons: React.ReactNode[]
		leftCtaButtons: React.ReactNode[]
	} => {
		const isBookAgainAvailable = Boolean(reservation.calendarEvent.service?.availableForReservation && salonServicesData)

		const bookAgainButton = (
			<Button key={'bookAgainButton'} buttonStyle={ButtonModel.Styles.primary} type={'button'} onClick={() => onBookAgain(reservation)}>
				{messages['Book again']}
			</Button>
		)

		const cancelReservationButton = (
			<Button key={'cancelReservationButton'} buttonStyle={ButtonModel.Styles.secondary} type={'button'} onClick={onCancelReservation}>
				{messages['Cancel the reservation']}
			</Button>
		)

		const addReviewButton = (
			<Button
				key={'addReviewButton'}
				buttonStyle={ButtonModel.Styles.secondary}
				type={'button'}
				href={PAGE_LINKS['/salons/:salonSlug'](locale, reservation.calendarEvent.salon.seoSlugName, {
					reservationID: reservation.id,
					t: queryParamsToken
				})}
			>
				{messages['Add review']}
			</Button>
		)

		const defaultLeftCtaButtons: React.ReactNode[] = []

		if (salonPhoneNumber) {
			defaultLeftCtaButtons.push(
				<Button key={'salonPhoneNumberButton'} buttonStyle={ButtonModel.Styles.secondary} type={'button'} href={`tel:${salonPhoneNumber}`}>
					{messages.Call}
				</Button>
			)
		}

		if (googleMapsLink) {
			defaultLeftCtaButtons.push(
				<Button
					key={'showOnMapButton'}
					buttonStyle={ButtonModel.Styles.secondary}
					type={'button'}
					href={googleMapsLink}
					target={'_blank'}
					// NOTE: notino-toolkit doesn't support rel attribute prop for Button component, so we need to use spread operator to silence the type error
					// but it is passed to the DOM element correctly
					{...{ rel: 'nofollow noopener noreferrer' }}
				>
					{messages['Show on map']}
				</Button>
			)
		}

		switch (reservation.state) {
			case 'PENDING': {
				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Reservation is waiting for confirmation'],
					description: reservation.awaitingApprovalUntilAt
						? formatMessage(
								{
									id: 'The reservation is currently awaiting confirmation, if the salon does not confirm it by { time } it will be automatically cancelled.',
									defaultMessage:
										'The reservation is currently awaiting confirmation, if the salon does not confirm it by { time } it will be automatically cancelled.'
								},
								{
									time: formatDate(reservation.awaitingApprovalUntilAt, {
										day: 'numeric',
										month: 'long',
										year: 'numeric',
										hour: 'numeric',
										minute: 'numeric'
									})
								}
							)
						: '',
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons: getDateTime(reservation.calendarEvent.start.date, reservation.calendarEvent.start.time).isSameOrAfter(dayjs(), 'minutes')
						? [cancelReservationButton]
						: []
				}
			}
			case 'APPROVED': {
				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Your reservation has been confirmed'],
					description: '',
					leftCtaButtons: [
						...defaultLeftCtaButtons,
						<Button
							key={'addToCalendarButton'}
							buttonStyle={ButtonModel.Styles.secondary}
							type={'button'}
							onClick={() => setIsAddToCalendarModalOpen(true)}
						>
							{messages['Add to calendar']}
						</Button>
					],
					rightCtaButtons: getDateTime(reservation.calendarEvent.start.date, reservation.calendarEvent.start.time).isSameOrAfter(dayjs(), 'minutes')
						? [cancelReservationButton]
						: []
				}
			}
			case 'CANCEL_BY_SALON':
			case 'CANCEL_BY_CUSTOMER': {
				let description = ''
				const cancellationAnswer = reservation.reservationCancellation?.quickFeedbackAnswers[0]

				if (cancellationAnswer) {
					description = cancellationAnswer.code !== 'OWN_MESSAGE' ? cancellationAnswer.name : (reservation.reservationCancellation?.text ?? '')
				}

				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Reservation is cancelled'],
					description,
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons: isBookAgainAvailable ? [bookAgainButton] : []
				}
			}
			case 'DECLINED': {
				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Reservation is cancelled'],
					description: '',
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons: isBookAgainAvailable ? [bookAgainButton] : []
				}
			}
			case 'NOT_REALIZED':
				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Completed reservation'],
					description: '',
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons: isBookAgainAvailable ? [bookAgainButton] : []
				}
			case 'REALIZED': {
				const rightCtaButtons = [addReviewButton]

				if (isBookAgainAvailable) {
					rightCtaButtons.push(bookAgainButton)
				}

				return {
					badge: <ReservationStatusBadge reservationState={reservation.state as RESERVATION_STATE} />,
					title: messages['Completed reservation'],
					description: '',
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons
				}
			}
			default:
				return {
					badge: null,
					title: '',
					description: '',
					leftCtaButtons: [...defaultLeftCtaButtons],
					rightCtaButtons: []
				}
		}
	}

	const stateSpecificProps = getReservationStateSpecificProps()

	return (
		<>
			<SC.ReservationContainer>
				{stateSpecificProps.badge && <SC.StatusBadgeWrapper>{stateSpecificProps.badge}</SC.StatusBadgeWrapper>}
				<SC.TitleWrapper>
					{stateSpecificProps.title && <SC.MainTitle>{stateSpecificProps.title}</SC.MainTitle>}
					{stateSpecificProps.description && <SC.Description>{stateSpecificProps.description}</SC.Description>}
					{reservation.customer?.isVisitor && (
						<SC.VisitorInfoMessage>
							<IconRegularGuest aria-hidden />
							{messages['This reservation was made for another guest.']}
						</SC.VisitorInfoMessage>
					)}
				</SC.TitleWrapper>
				<SC.SalonInfoContainer>
					<SalonInfo salonData={reservation.calendarEvent.salon} />
				</SC.SalonInfoContainer>
				<SC.CtaButtonsContainer>
					{stateSpecificProps.leftCtaButtons.length ? (
						<SC.LeftCtaButtonsContainer>{stateSpecificProps.leftCtaButtons}</SC.LeftCtaButtonsContainer>
					) : null}
					{stateSpecificProps.rightCtaButtons.length ? (
						<SC.RightCtaButtonsContainer>{stateSpecificProps.rightCtaButtons}</SC.RightCtaButtonsContainer>
					) : null}
				</SC.CtaButtonsContainer>
				<SC.InfoList>
					{/* Service */}
					{reservation.calendarEvent.service && (
						<SC.InfoListItem>
							<SC.InfoItemIconContainer>
								<ScissorsIcon />
							</SC.InfoItemIconContainer>
							<SC.InfoItemContent>
								<SC.InfoContentServiceLayout>
									<SC.InfoContentService>
										<SC.InfoItemTitle>{messages.Service}</SC.InfoItemTitle>
										<SC.InfoItemText>{reservation.calendarEvent.service.name}</SC.InfoItemText>
										{serviceParameterLabel && <SC.InfoItemDescription>{serviceParameterLabel}</SC.InfoItemDescription>}
									</SC.InfoContentService>
									{reservation.price.priceFrom && (
										<SC.InfoContentServicePrice
											from={reservation.price.priceFrom}
											to={reservation.price.priceTo}
											fromSecondary={reservation.price.priceFromSecondary}
											toSecondary={reservation.price.priceToSecondary}
										/>
									)}
								</SC.InfoContentServiceLayout>
							</SC.InfoItemContent>
						</SC.InfoListItem>
					)}
					{/* Employee */}
					{employeeName && (
						<SC.InfoListItem>
							<SC.InfoItemIconContainer>
								<IconRegularPerson width={'1.25rem'} height={'1.25rem'} color={'icon.primary'} />
							</SC.InfoItemIconContainer>
							<SC.InfoItemContent>
								<SC.InfoItemTitle>{messages.Employee}</SC.InfoItemTitle>
								<SC.InfoItemText>{employeeName}</SC.InfoItemText>
							</SC.InfoItemContent>
						</SC.InfoListItem>
					)}

					{/* Date and time */}
					<SC.InfoListItem>
						<SC.InfoItemIconContainer>
							<IconRegularCalendar width={'1.25rem'} height={'1.25rem'} color={'icon.primary'} />
						</SC.InfoItemIconContainer>
						<SC.InfoItemContent>
							<SC.InfoItemTitle>{messages.Date}</SC.InfoItemTitle>
							<SC.InfoItemText>{dateTimeRange}</SC.InfoItemText>
						</SC.InfoItemContent>
					</SC.InfoListItem>
					{/* Capacity */}
					{reservation.calendarEvent.capacity.occupiedReservationsCount >= 0 &&
						reservation.calendarEvent.capacity.maxReservationsCount &&
						reservation.calendarEvent.eventType === EVENT_TYPE.GROUP_RESERVATION && (
							<SC.InfoListItem>
								<SC.InfoItemIconContainer>
									<UsersIcon />
								</SC.InfoItemIconContainer>
								<SC.InfoItemContent>
									<SC.InfoContentServiceLayout>
										<SC.InfoContentService>
											<SC.InfoItemTitle>{messages.Capacity}</SC.InfoItemTitle>
											<SC.InfoItemText>{`${reservation.calendarEvent.capacity.occupiedReservationsCount} / ${reservation.calendarEvent.capacity.maxReservationsCount}`}</SC.InfoItemText>
										</SC.InfoContentService>
									</SC.InfoContentServiceLayout>
								</SC.InfoItemContent>
							</SC.InfoListItem>
						)}
					{/* Note */}
					{reservation.noteFromB2CCustomer && (
						<SC.InfoListItem>
							<SC.InfoItemIconContainer>
								<IconRegularPen width={'1.25rem'} height={'1.25rem'} color={'icon.primary'} />
							</SC.InfoItemIconContainer>
							<SC.InfoItemContent>
								<SC.InfoItemTitle>{messages.Note}</SC.InfoItemTitle>
								<SC.InfoItemText>{reservation.noteFromB2CCustomer}</SC.InfoItemText>
							</SC.InfoItemContent>
						</SC.InfoListItem>
					)}
					{/* Customer */}
					{reservation.customer && (
						<SC.InfoListItem>
							<SC.InfoItemIconContainer>
								<IconRegularPerson width={'1.25rem'} height={'1.25rem'} color={'icon.primary'} aria-hidden />
							</SC.InfoItemIconContainer>
							<SC.InfoItemContent>
								<SC.InfoItemTitle>{messages.Customer}</SC.InfoItemTitle>
								<SC.CustomerInfo>
									{reservation.customer.firstName && reservation.customer.lastName && (
										<SC.CustomerName>{`${reservation.customer.firstName} ${reservation.customer.lastName}`}</SC.CustomerName>
									)}
									{reservation.customer.email && <SC.CustomerInfoText>{reservation.customer.email}</SC.CustomerInfoText>}
									{reservation.customer.phonePrefix && reservation.customer.phone && (
										<SC.CustomerInfoText>{`${reservation.customer.phonePrefix} ${reservation.customer.phone}`}</SC.CustomerInfoText>
									)}
								</SC.CustomerInfo>
							</SC.InfoItemContent>
						</SC.InfoListItem>
					)}
				</SC.InfoList>
			</SC.ReservationContainer>
			{isAddToCalendarModalOpen && (
				<AddToCalendarModal
					show
					onClose={() => setIsAddToCalendarModalOpen(false)}
					calendarFile={reservation.calendarFile}
					reservationID={reservation.id}
				/>
			)}
		</>
	)
}

export default Reservation
